import { Store } from "vuex";
import { PortalStore } from "./helpers/portal-store-helper";
import { SiteStore } from "./helpers/site-store-helper";
import { UserStore } from "./helpers/user-store-helper";
import { PortalInformationStore } from "./helpers/portal-information-store-helper";
import { PortalHyperlinksStore } from "./helpers/portal-hyperlinks-store-helper";
import { NewsArticleStore } from "./helpers/news-article-store-helper";
import { SettingsStore } from "./helpers/settings-store-helper";
import { ContentItemStore } from "./helpers/content-items-store-helper";
import { FavoriteStore } from "./helpers/favorite-store-helper";
import { SearchFeebackStore } from "./helpers/search-feedback-store-helper";
import { SuggestionsStore } from "./helpers/suggestions-store-helper";
import { AppsStore } from "./helpers/apps-store-helper";
import { SubscriptionsStore } from "./helpers/subscriptions-store-helper";
import { TasksStore } from "./helpers/tasks-store-helper";
import { ContentItemFilterStore } from "./helpers/content-items-filters-store-helper";
import { TeamsStore } from "./helpers/teams-store-helper";
import { HyperlinksStore } from "./helpers/hyperlinks-store-helper";
import { CollectionsStore } from "./helpers/collections-store-helper";
import { DocumentStore } from "./helpers/document-store-helper";
import { PendingDocumentCommentStore } from "./helpers/pending-document-comments-store-helper";
import { ReadListStore } from "./helpers/read-list-store-helper";
import { NotificationsStore } from "./helpers/notifications-store-helper";
import Glossary from "./helpers/glossary-store-helper";
import { FeaturesStore } from "./helpers/features-store-helper";
import TimeZonesStore from "./helpers/timezones-store-helper";
import AiSearch from "./helpers/ai-search-helper";
import AiBrowse from "./helpers/ai-browse-helper";

export default class {
    /** Private pointers to the stores */
    private static site: SiteStore;
    private static user: UserStore;
    private static portal: PortalStore;
    private static portalInformation: PortalInformationStore;
    private static portalHyperlinks: PortalHyperlinksStore;
    private static newsArticle: NewsArticleStore;
    private static settings: SettingsStore;
    private static contentItem: ContentItemStore;
    private static favorite: FavoriteStore;
    private static searchFeedback: SearchFeebackStore;
    private static suggestions: SuggestionsStore;
    private static apps: AppsStore;
    private static subscriptions: SubscriptionsStore;
    private static tasks: TasksStore;
    private static contentItemFilter: ContentItemFilterStore;
    private static teams: TeamsStore;
    private static hyperlink: HyperlinksStore;
    private static collections: CollectionsStore;
    private static document: DocumentStore;
    private static pendingComment: PendingDocumentCommentStore;
    private static readList: ReadListStore;
    private static notifications: NotificationsStore;
    private static glossary: Glossary;
    private static timeZone: TimeZonesStore;
    private static features: FeaturesStore;
    private static aiSearch: AiSearch;
    private static aiBrowse: AiBrowse;

    /** Gets the site module */
    public static get siteStore(): SiteStore {
        return this.site;
    }

    /** Gets the user module */
    public static get userStore(): UserStore {
        return this.user;
    }

    /** Gets the portal module */
    public static get portalStore(): PortalStore {
        return this.portal;
    }

    /** Gets the portal information module */
    public static get portalInformationStore(): PortalInformationStore {
        return this.portalInformation;
    }

    /** Gets the portal hyperlinks module */
    public static get portalHyperlinksStore(): PortalHyperlinksStore {
        return this.portalHyperlinks;
    }

    /** Gets the news module */
    public static get newsArticleStore(): NewsArticleStore {
        return this.newsArticle;
    }

    /** Gets the settings module */
    public static get settingsStore(): SettingsStore {
        return this.settings;
    }

    /** Gets the content items module */
    public static get contentItemsStore(): ContentItemStore {
        return this.contentItem;
    }

    /** Gets the favorite module */
    public static get favoriteItemsStore(): FavoriteStore {
        return this.favorite;
    }

    /** Gets the search feedback module */
    public static get searchFeedbackStore(): SearchFeebackStore {
        return this.searchFeedback;
    }
    /** Gets the apps module */
    public static get appsStore(): AppsStore {
        return this.apps;
    }
    //**Gets the teams module */
    public static get teamsStore(): TeamsStore {
        return this.teams;
    }
    //*Gets the suggestions module */
    public static get suggestionsStore(): SuggestionsStore {
        return this.suggestions;
    }

    /** Gets the subscriptions module */
    public static get subscriptionsStore(): SubscriptionsStore {
        return this.subscriptions;
    }

    /** Gets the tasks module */
    public static get tasksStore(): TasksStore {
        return this.tasks;
    }

    // * Gets the content item filters module */
    public static get contentItemFiltersStore(): ContentItemFilterStore {
        return this.contentItemFilter;
    }

    //* Gets the hyperlink module */
    public static get hyperlinksStore(): HyperlinksStore {
        return this.hyperlink;
    }

    //* Gets the document module */
    public static get documentStore(): DocumentStore {
        return this.document;
    }

    //* Gets the pending comments module */
    public static get pendingDocumentCommentStore(): PendingDocumentCommentStore {
        return this.pendingComment;
    }

    //* Gets the read list module */
    public static get readListStore(): ReadListStore {
        return this.readList;
    }

    //* Gets the notifications module */
    public static get notificationsStore(): NotificationsStore {
        return this.notifications;
    }

    // * Gets the collections module */
    public static get collectionsStore(): CollectionsStore {
        return this.collections;
    }

    public static get glossaryStore(): Glossary {
        return this.glossary;
    }

    public static get timeZonesStore(): TimeZonesStore {
        return this.timeZone;
    }

    // * Gets the features module */
    public static get FeaturesStore(): FeaturesStore {
        return this.features;
    }

    public static get aiSearchStore(): AiSearch {
        return this.aiSearch;
    }

    public static get aiBrowseStore(): AiBrowse {
        return this.aiBrowse;
    }

    /**
     * Initializes the store helper (provides a pointer to the store)
     * @param store The store the helper is being initialized for
     */
    public static init(store: Store<any>): void {
        this.site = new SiteStore(store);
        this.user = new UserStore(store);
        this.portal = new PortalStore(store);
        this.portalInformation = new PortalInformationStore(store);
        this.portalHyperlinks = new PortalHyperlinksStore(store);
        this.newsArticle = new NewsArticleStore(store);
        this.settings = new SettingsStore(store);
        this.contentItem = new ContentItemStore(store);
        this.favorite = new FavoriteStore(store);
        this.searchFeedback = new SearchFeebackStore(store);
        this.suggestions = new SuggestionsStore(store);
        this.apps = new AppsStore(store);
        this.subscriptions = new SubscriptionsStore(store);
        this.tasks = new TasksStore(store);
        this.contentItemFilter = new ContentItemFilterStore(store);
        this.teams = new TeamsStore(store);
        this.hyperlink = new HyperlinksStore(store);
        this.collections = new CollectionsStore(store);
        this.document = new DocumentStore(store);
        this.pendingComment = new PendingDocumentCommentStore(store);
        this.readList = new ReadListStore(store);
        this.notifications = new NotificationsStore(store);
        this.glossary = new Glossary(store);
        this.timeZone = new TimeZonesStore(store);
        this.features = new FeaturesStore(store);
        this.aiSearch = new AiSearch(store);
        this.aiBrowse = new AiBrowse(store);
    }
}
